const pad = (n) => String(n).padStart(2, "0");
const ymd = (d) => (typeof d === "string" ? d : d.toISOString()).slice(0, 10);
const fmtHM = (mins) => `${pad(Math.floor(mins / 60))}:${pad(mins % 60)}`;
const timeToMins = (s) => {
  const [h, m] = s.split(":").map(Number);
  return h * 60 + m;
};

const isTodayOrFuture = (dateStr) => {
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return false;

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  date.setHours(0, 0, 0, 0);

  return date > today;
};

const fmtHourLabel = (h) => {
  const ampm = h >= 12 ? "PM" : "AM";
  const hour12 = h % 12 || 12; // 0 or 12 -> 12
  return `${hour12}:00 ${ampm}`;
};

const hhmmssToMins = (t) => {
  if (!t) return null;
  const [h, m] = t.split(":").map(Number); // ignore seconds
  return h * 60 + m;
};

export const formatTo12Hour = (dateTimeStr) => {
  if (!dateTimeStr) return "";

  const date = new Date(dateTimeStr.replace(" ", "T")); // Safe parsing

  let hours = date.getHours();
  let minutes = date.getMinutes();

  const ampm = hours >= 12 ? "PM" : "AM";
  hours = hours % 12 || 12; // Convert 0 → 12

  const formattedMinutes = minutes.toString().padStart(2, "0");

  return `${hours}:${formattedMinutes} ${ampm}`;
};

const fmtHourLabelInto24 = (h) => {
  const n = Number(h);
  if (!Number.isFinite(n)) return String(h);
  // If your scale uses 0..24, keep the terminal label as 24:00
  if (n === 24) return "24:00"; // change to "00:00" if you prefer rollover
  return `${pad(n)}:00`;
};

const minsToTime = (mins) => fmtHM(mins);

const formatLong = (d) => {
  const dt = d instanceof Date ? d : new Date(d);
  return dt.toLocaleDateString(undefined, {
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

// Parse "YYYY-MM-DD HH:mm:ss" or "YYYY-MM-DDTHH:mm:ss" into local Date
const parseYmdHmsToLocal = (s) => {
  if (!s || typeof s !== "string") return null;
  const m = s.match(
    /^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2})(?::(\d{2}))?)?$/
  );
  if (!m) return null;
  const year = Number(m[1]);
  const month = Number(m[2]) - 1;
  const day = Number(m[3]);
  const hour = Number(m[4] ?? 0);
  const minute = Number(m[5] ?? 0);
  const second = Number(m[6] ?? 0);
  return new Date(year, month, day, hour, minute, second, 0);
};

// convert a Date -> minutes from 00:00 (0..1439). Works in local time.
const dateToMinutes = (d) => d.getHours() * 60 + d.getMinutes();

// convert a Date with seconds into minutes, rounding seconds up to next minute
const dateToMinutesCeilSeconds = (d) => {
  const base = d.getHours() * 60 + d.getMinutes();
  return d.getSeconds() > 0 ? base + 1 : base;
};

const rid = () => Math.random().toString(36).slice(2, 9);

const defaultTables = (n) =>
  Array.from({ length: n }, (_, i) => ({ id: i + 1, name: `Table ${i + 1}` }));

const hmToMin = (s) => {
  if (!s) return 0;
  const [h, m] = s.split(":").map(Number);
  return h * 60 + (m || 0);
};

const norm = (v) =>
  String(v ?? "")
    .trim()
    .toLowerCase();

const toDayIndex = (iso) => {
  if (!iso) return Math.floor(Date.now() / 86400000);
  const [y, m, d] = iso.split("-").map(Number);
  return Math.floor(Date.UTC(y, m - 1, d) / 86400000);
};

const fromDayIndex = (idx) => {
  const ms = idx * 86400000;
  const d = new Date(ms); // UTC midnight
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, "0");
  const day = String(d.getUTCDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const addDaysStr = (iso, delta) => fromDayIndex(toDayIndex(iso) + delta);

const to12Hour = (timeStr) => {
  if (!timeStr) return "";

  const [hStr, mStr] = timeStr.split(":"); // "HH:MM"
  let h = Number(hStr);
  if (Number.isNaN(h)) return timeStr;

  const suffix = h >= 12 ? "PM" : "AM";
  h = h % 12;
  if (h === 0) h = 12;

  return `${h}:${mStr} ${suffix}`;
};

const timeToMinutes = (timeStr) => {
  if (!timeStr) return null;

  const parts = timeStr.split(":");
  const hh = Number(parts[0] ?? "0");
  const mm = Number(parts[1] ?? "0");
  if (Number.isNaN(hh) || Number.isNaN(mm)) return null;

  return hh * 60 + mm;
};

const toKey = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? String(n) : String(v);
};

const toNumIfNumeric = (v) => {
  const n = Number(v);
  return Number.isFinite(n) && !Number.isNaN(n) ? n : v;
};



export {
  pad,
  ymd,
  fmtHM,
  timeToMins,
  isTodayOrFuture,
  fmtHourLabel,
  hhmmssToMins,
  fmtHourLabelInto24,
  minsToTime,
  formatLong,
  parseYmdHmsToLocal,
  dateToMinutes,
  dateToMinutesCeilSeconds,
  rid,
  defaultTables,
  hmToMin,
  norm,
  toDayIndex,
  fromDayIndex,
  addDaysStr,
  to12Hour,
  timeToMinutes,
  toKey,
  toNumIfNumeric,
};
